import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Link } from "@tanstack/react-router";
import { ArrowRight, BadgeCheck, ExternalLink, Globe, Loader2, RefreshCw, Unlink } from "lucide-react";
import { toast } from "sonner";
import {
  getNuvatrStatus,
  getNuvatrDraft,
  syncNuvatr,
  disconnectNuvatr,
  type NuvatrStatus,
  type NuvatrDraft,
} from "@/lib/nuvatr.functions";

// The Nuvatr connection card — the workspace half of the promise made on
// /nuvatr. The flow is deliberately three steps: 1) an active plan, 2) review
// and correct the company data, 3) publish. Nothing reaches the public
// registry before the customer has seen exactly what will be published.
export function NuvatrSection() {
  const load = useServerFn(getNuvatrStatus);
  const loadDraft = useServerFn(getNuvatrDraft);
  const sync = useServerFn(syncNuvatr);
  const unlink = useServerFn(disconnectNuvatr);

  const [status, setStatus] = useState<NuvatrStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState<NuvatrDraft | null>(null);
  const [reviewing, setReviewing] = useState(false);

  const refresh = () => load().then(setStatus).catch((e: Error) => toast.error(e.message));
  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function openReview() {
    setBusy(true);
    try {
      const d = await loadDraft();
      setDraft(d);
      setReviewing(true);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function publish() {
    if (!draft) return;
    setBusy(true);
    try {
      await sync({ data: { profile: draft } });
      toast.success("Your Nuvatr profile is up to date.");
      setReviewing(false);
      await refresh();
    } catch (e) {
      toast.error((e as Error).message);
      await refresh();
    } finally {
      setBusy(false);
    }
  }

  async function runDisconnect() {
    setBusy(true);
    try {
      await unlink();
      toast.success("Nuvatr disconnected.");
      await refresh();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const connection = status?.connection ?? null;
  const connected = connection?.status === "connected";

  const field = (
    label: string,
    value: string,
    onChange: (v: string) => void,
    textarea = false,
  ) => (
    <label className="block">
      <span className="text-xs text-muted-foreground">{label}</span>
      {textarea ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          className="mt-1 w-full rounded-lg border border-border bg-transparent px-3 py-2 text-sm outline-none focus:border-foreground/40"
        />
      ) : (
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="mt-1 w-full rounded-lg border border-border bg-transparent px-3 py-2 text-sm outline-none focus:border-foreground/40"
        />
      )}
    </label>
  );

  return (
    <div className="glass-strong rounded-2xl p-6">
      <div className="flex items-center gap-2 mb-1">
        <Globe className="h-4 w-4 text-[oklch(0.68_0.19_290)]" />
        <div className="font-display font-semibold">Nuvatr Global Registry</div>
      </div>
      <p className="text-xs text-muted-foreground mb-4">
        Publish your Twin&apos;s knowledge as a verified Nuvatr profile with a Global Trade Number —
        CHF {status?.listingPriceChf ?? 5} instead of CHF {status?.listPriceChf ?? 25}, plus up to{" "}
        {status?.discountPercent ?? 30}% off every Nuvatr service while your subscription is active.{" "}
        <Link to="/nuvatr" className="underline hover:text-foreground">
          How it works
        </Link>
      </p>

      {!status ? (
        <div className="text-sm text-muted-foreground py-4">Loading…</div>
      ) : !status.eligible ? (
        <div className="rounded-xl border border-border p-4 text-sm">
          <div className="font-medium">Step 1 — choose a paid plan</div>
          <p className="mt-1 text-xs text-muted-foreground">
            Nuvatr benefits are included with every paid Reality Twin plan. Complete your
            subscription first; afterwards you review your company data and only then is the profile
            published to Nuvatr. Benefits pause automatically if the plan lapses.
          </p>
          <ol className="mt-3 space-y-1 text-xs text-muted-foreground">
            <li>1. Activate your plan</li>
            <li>2. Review and correct your company data</li>
            <li>3. Publish to the Nuvatr registry</li>
          </ol>
          <Link
            to="/billing"
            className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-foreground px-4 py-2 text-[13px] font-medium text-background hover:opacity-90 transition"
          >
            Choose a plan <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      ) : (
        <div className="space-y-3">
          {connected && (
            <div className="rounded-xl border border-border p-4 text-sm space-y-1.5">
              <div className="flex items-center gap-2 font-medium">
                <BadgeCheck className="h-4 w-4 text-[oklch(0.82_0.18_168)]" />
                Connected
              </div>
              {connection?.nuvatrId && (
                <div className="text-xs text-muted-foreground">
                  Global Trade Number: <code className="px-1 rounded bg-white/5">{connection.nuvatrId}</code>
                </div>
              )}
              {connection?.discountCode && (
                <div className="text-xs text-muted-foreground">
                  Discount code:{" "}
                  <code className="px-1 rounded bg-white/5">{connection.discountCode}</code>
                </div>
              )}
              {connection?.lastSyncedAt && (
                <div className="text-xs text-muted-foreground">
                  Last sync: {new Date(connection.lastSyncedAt).toLocaleString()}
                </div>
              )}
              {connection?.profileUrl && (
                <a
                  href={connection.profileUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs underline hover:text-foreground"
                >
                  Open Nuvatr profile <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </div>
          )}

          {connection?.lastError && (
            <div className="rounded-xl border border-destructive/40 p-4 text-xs text-destructive">
              Last attempt failed: {connection.lastError}
            </div>
          )}

          {!status.configured && (
            <div className="rounded-xl border border-border p-4 text-xs text-muted-foreground">
              The Nuvatr partner connection is not configured on this environment yet.
            </div>
          )}

          {reviewing && draft ? (
            <div className="rounded-xl border border-border p-4 space-y-3">
              <div>
                <div className="text-sm font-medium">
                  {connected ? "Review before syncing" : "Review before publishing"}
                </div>
                <p className="text-xs text-muted-foreground">
                  This is exactly what Nuvatr will show. Changes are saved to your Twin as well.
                </p>
              </div>
              {field("Company name", draft.name ?? "", (v) => setDraft({ ...draft, name: v }))}
              {field("Website", draft.website ?? "", (v) => setDraft({ ...draft, website: v }))}
              {field(
                "Description",
                draft.description ?? "",
                (v) => setDraft({ ...draft, description: v }),
                true,
              )}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {field("Industry", draft.industry ?? "", (v) => setDraft({ ...draft, industry: v }))}
                {field("Country", draft.country ?? "", (v) => setDraft({ ...draft, country: v }))}
                {field("Company size", draft.size ?? "", (v) => setDraft({ ...draft, size: v }))}
              </div>
              {field("Services (comma separated)", draft.services.join(", "), (v) =>
                setDraft({
                  ...draft,
                  services: v.split(",").map((s) => s.trim()).filter(Boolean),
                }),
              )}
              {field("Looking for (comma separated)", draft.seeking.join(", "), (v) =>
                setDraft({
                  ...draft,
                  seeking: v.split(",").map((s) => s.trim()).filter(Boolean),
                }),
              )}
              <div className="flex flex-wrap gap-2 pt-1">
                <button
                  type="button"
                  onClick={publish}
                  disabled={busy || !status.configured}
                  className="inline-flex items-center gap-1.5 rounded-full bg-foreground px-4 py-2 text-[13px] font-medium text-background hover:opacity-90 transition disabled:opacity-50"
                >
                  {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ArrowRight className="h-3.5 w-3.5" />}
                  {connected ? "Save & sync" : "Publish to Nuvatr"}
                </button>
                <button
                  type="button"
                  onClick={() => setReviewing(false)}
                  disabled={busy}
                  className="inline-flex items-center gap-1.5 rounded-full border border-border px-4 py-2 text-[13px] font-medium hover:bg-white/5 transition disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={openReview}
                disabled={busy || !status.canManage || !status.configured}
                className="inline-flex items-center gap-1.5 rounded-full bg-foreground px-4 py-2 text-[13px] font-medium text-background hover:opacity-90 transition disabled:opacity-50"
              >
                {busy ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : connected ? (
                  <RefreshCw className="h-3.5 w-3.5" />
                ) : (
                  <ArrowRight className="h-3.5 w-3.5" />
                )}
                {connected ? "Review & sync" : "Review company data"}
              </button>
              {connected && (
                <button
                  type="button"
                  onClick={runDisconnect}
                  disabled={busy || !status.canManage}
                  className="inline-flex items-center gap-1.5 rounded-full border border-border px-4 py-2 text-[13px] font-medium hover:bg-white/5 transition disabled:opacity-50"
                >
                  <Unlink className="h-3.5 w-3.5" /> Disconnect
                </button>
              )}
            </div>
          )}

          {!status.canManage && (
            <p className="text-xs text-muted-foreground">
              Only owners and admins can manage the Nuvatr connection.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
